import { FieldArray, Form, Formik, useField, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import * as Yup from "yup";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import type { Client } from "@/features/clients/types";
import {
  type AuthorizationMode,
  DESIGNATION_OPTIONS,
  FORM_3520_PRIVILEGES,
  type Form3520Data,
  type FormSubmission,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox } from "./formik-controls";

const MAX_YEAR_RANGES = 4;

const DESIGNATION_LABEL = new Map<string, string>(DESIGNATION_OPTIONS.map((o) => [o.value, o.label]));

const schema = Yup.object({
  authorization: Yup.object({
    mode: Yup.string().oneOf(["all", "specific"]).required(),
    years: Yup.array()
      .of(Yup.object({ from: Yup.string().max(10), to: Yup.string().max(10) }))
      .max(MAX_YEAR_RANGES),
  }),
});

function toInitialValues(data: Record<string, unknown>): Form3520Data {
  const d = data as Partial<Form3520Data>;
  const auth = d.authorization ?? ({} as Partial<Form3520Data["authorization"]>);
  const priv = d.privileges ?? ({} as Partial<Form3520Data["privileges"]>);
  return {
    for_who: d.for_who ?? "taxpayer",
    representatives: d.representatives ?? [],
    authorization: {
      mode: auth.mode ?? "all",
      years: auth.years && auth.years.length > 0 ? auth.years : [{ from: "", to: "" }],
    },
    privileges: {
      add_rep: priv.add_rep ?? false,
      sign_absent: priv.sign_absent ?? false,
      receive_check: priv.receive_check ?? false,
      waive_sol: priv.waive_sol ?? false,
      exec_settlements: priv.exec_settlements ?? false,
      other_priv: priv.other_priv ?? false,
      online_access: priv.online_access ?? false,
    },
    other_acts: d.other_acts ?? "",
    ca_corp_no: d.ca_corp_no ?? "",
    ca_sos_no: d.ca_sos_no ?? "",
    contact_name: d.contact_name ?? "",
    contact_title: d.contact_title ?? "",
  };
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

function RepresentativesTable() {
  const { values } = useFormikContext<Form3520Data>();
  const reps = values.representatives;
  if (reps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No representatives are assigned to this client. Add them on the client's profile,
        then reopen this form.
      </p>
    );
  }
  return (
    <Card className="gap-0 overflow-hidden overflow-x-auto p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Active</TableHead>
            <TableHead>Representative</TableHead>
            <TableHead className="w-48">Designation</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reps.map((rep, i) => (
            <TableRow key={rep.practitioner || i}>
              <TableCell>
                <FormikCheckbox name={`representatives.${i}.active`} aria-label={`Active ${rep.name}`} />
              </TableCell>
              <TableCell className="font-medium">{rep.name}</TableCell>
              <TableCell className="text-muted-foreground">
                {DESIGNATION_LABEL.get(rep.designation) ?? rep.designation ?? "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function AuthorizationSection() {
  const [modeField, , modeHelpers] = useField<AuthorizationMode>("authorization.mode");
  const { values } = useFormikContext<Form3520Data>();
  return (
    <div className="flex flex-col gap-4">
      <RadioGroup
        className="flex flex-wrap gap-6"
        value={modeField.value}
        onValueChange={(v) => modeHelpers.setValue(v as AuthorizationMode)}
      >
        <Label htmlFor="auth-all" className="flex items-center gap-2 font-normal">
          <RadioGroupItem id="auth-all" value="all" /> All tax years / income periods
        </Label>
        <Label htmlFor="auth-specific" className="flex items-center gap-2 font-normal">
          <RadioGroupItem id="auth-specific" value="specific" /> Specific tax years / periods
        </Label>
      </RadioGroup>

      {values.authorization.mode === "specific" && (
        <FieldArray name="authorization.years">
          {(helpers) => (
            <div className="flex flex-col gap-3">
              {values.authorization.years.map((_, i) => (
                <div key={i} className="flex items-end gap-3">
                  <div className="grid gap-1">
                    <Label className="text-muted-foreground text-xs" htmlFor={`authorization.years.${i}.from`}>
                      From
                    </Label>
                    <FormikCell
                      name={`authorization.years.${i}.from`}
                      className="w-32"
                      placeholder="e.g. 2021"
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-muted-foreground text-xs" htmlFor={`authorization.years.${i}.to`}>
                      To
                    </Label>
                    <FormikCell
                      name={`authorization.years.${i}.to`}
                      className="w-32"
                      placeholder="e.g. 2023"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove year range ${i + 1}`}
                    disabled={values.authorization.years.length === 1}
                    onClick={() => helpers.remove(i)}
                  >
                    <Trash2 className="text-muted-foreground size-4" />
                  </Button>
                </div>
              ))}
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={values.authorization.years.length >= MAX_YEAR_RANGES}
                  onClick={() => helpers.push({ from: "", to: "" })}
                >
                  <Plus /> Add year range
                </Button>
              </div>
            </div>
          )}
        </FieldArray>
      )}
    </div>
  );
}

function OtherActsField() {
  const [field] = useField("other_acts");
  return (
    <div className="grid gap-2">
      <Label htmlFor="other_acts">Description of other acts</Label>
      <Textarea id="other_acts" rows={3} {...field} />
    </div>
  );
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable CA FTB Form 3520 (Power of Attorney) — prefilled from the client +
 * its practitioners. Save persists the data; Preview PDF opens the filled
 * (PIT for individuals / BE for businesses) PDF; Save to Documents stores it.
 */
export function Form3520Editor({ client, submission, onSubmissionChange }: Props) {
  const isBusiness = client.type === "Business";

  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  const privileges = FORM_3520_PRIVILEGES.filter((p) => p.businessToo || !isBusiness);

  return (
    <Formik initialValues={toInitialValues(submission.data)} validationSchema={schema} onSubmit={() => {}}>
      <Form className="flex flex-col gap-6">
          <Card className="gap-3 p-6">
            <p className="text-foreground text-lg font-semibold">
              {client.name}{" "}
              <span className="text-muted-foreground text-sm font-normal">
                ({isBusiness ? "Business — Form 3520-BE" : "Individual — Form 3520-PIT"})
              </span>
            </p>
            {address && <p className="text-muted-foreground text-sm">{address}</p>}
            {isBusiness && (
              <div className="mt-2 grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="ca_corp_no">CA Corporation No.</Label>
                  <FormikCell name="ca_corp_no" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ca_sos_no">CA Secretary of State No.</Label>
                  <FormikCell name="ca_sos_no" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="contact_name">Contact person</Label>
                  <FormikCell name="contact_name" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="contact_title">Contact title</Label>
                  <FormikCell name="contact_title" />
                </div>
              </div>
            )}
          </Card>

          <section className="flex flex-col gap-4">
            <SectionHeading>Representative(s)</SectionHeading>
            <RepresentativesTable />
          </section>

          <Card className="gap-4 p-6">
            <SectionHeading>Authorization</SectionHeading>
            <AuthorizationSection />
          </Card>

          <Card className="gap-4 p-6">
            <SectionHeading>Privileges</SectionHeading>
            <div className="grid gap-3 sm:grid-cols-2">
              {privileges.map((p) => (
                <FormikCheckbox key={p.key} name={`privileges.${p.key}`} label={p.label} />
              ))}
            </div>
            <OtherActsField />
          </Card>

          <FormEditorActions
            submission={submission}
            clientId={client.id}
            onSubmissionChange={onSubmissionChange}
          />
        </Form>
    </Formik>
  );
}
